import { toast } from "sonner";

// Shopify Configuration
const SHOPIFY_API_VERSION = '2026-10';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'e4928e-48.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = 'fa610c1121da6777fb385332c1c466b7';

// Types
export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    tags?: string[];
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

export interface CartItemSubscription {
  enabled: boolean;
  plan: string;
  termMonths: number;
  frequency: string;
  monthIndex: number;
  pricePerPack: number;
  packType: "3-pack" | "12-pack" | "single" | "2-pack";
  subscriptionKey: string;
  baseVariantId: string;
}

export interface CartItem {
  lineId?: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  subscription?: CartItemSubscription;
}

// GraphQL Queries
const STOREFRONT_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          tags
          productType
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

const GET_PRODUCT_BY_HANDLE = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      productType
      tags
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

// Query for inventory levels with product info
const GET_INVENTORY_LEVELS = `
  query GetProductInventory($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          totalInventory
          variants(first: 50) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
              }
            }
          }
        }
      }
    }
  }
`;

export interface InventoryData {
  productId: string;
  variantId?: string;
  status: 'in-stock' | 'low-stock' | 'backorder' | 'out-of-stock';
  quantity: number;
  leadTime?: string;
}

// Cart GraphQL Queries
const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      totalQuantity
      checkoutUrl
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_DISCOUNT_CODES_UPDATE = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        checkoutUrl
        discountCodes {
          code
          applicable
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// API Helper
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (response.status === 402) {
    toast.error("Shopify: Payment required", {
      description: "Shopify API access requires an active billing plan. Visit https://admin.shopify.com to upgrade.",
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  return data;
}

// Fetch Products
export async function fetchShopifyProducts(first: number = 50, query?: string): Promise<ShopifyProduct[]> {
  const data = await storefrontApiRequest(STOREFRONT_QUERY, { first, query });
  if (!data) return [];
  return data.data.products.edges;
}

// Fetch Single Product by Handle
export async function fetchProductByHandle(handle: string) {
  const data = await storefrontApiRequest(GET_PRODUCT_BY_HANDLE, { handle });
  if (!data) return null;
  return data.data.productByHandle;
}

// Helper Functions
function formatCheckoutUrl(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set('channel', 'online_store');
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

interface UserError {
  field: string[] | null;
  message: string;
}

function isCartNotFoundError(userErrors: UserError[]): boolean {
  return userErrors.some(e => 
    e.message.toLowerCase().includes('cart not found') || 
    e.message.toLowerCase().includes('does not exist')
  );
}

// Fetch inventory levels for products
export async function fetchProductInventory(productIds?: string[]): Promise<Map<string, InventoryData>> {
  const inventoryMap = new Map<string, InventoryData>();
  
  try {
    // Build query filter if specific product IDs provided
    let query: string | undefined;
    if (productIds && productIds.length > 0) {
      // Extract numeric IDs from GraphQL IDs
      const numericIds = productIds.map(id => {
        const match = id.match(/(\d+)$/);
        return match ? match[1] : id;
      });
      query = numericIds.map(id => `id:${id}`).join(' OR ');
    }
    
    const data = await storefrontApiRequest(GET_INVENTORY_LEVELS, { 
      first: productIds?.length || 100,
      query 
    });
    
    if (!data?.data?.products?.edges) return inventoryMap;
    
    for (const product of data.data.products.edges) {
      const productNode = product.node;
      const totalInventory = productNode.totalInventory || 0;
      
      // Determine product-level status
      let status: InventoryData['status'] = 'in-stock';
      if (totalInventory <= 0) {
        status = 'out-of-stock';
      } else if (totalInventory <= 10) {
        status = 'low-stock';
      }
      
      // Store product-level inventory
      inventoryMap.set(productNode.id, {
        productId: productNode.id,
        status,
        quantity: totalInventory,
        leadTime: status === 'out-of-stock' ? '2-3 weeks' : undefined,
      });
      
      // Store variant-level inventory
      for (const variant of productNode.variants.edges) {
        const variantNode = variant.node;
        const variantQty = variantNode.quantityAvailable || 0;
        
        let variantStatus: InventoryData['status'] = 'in-stock';
        if (!variantNode.availableForSale || variantQty <= 0) {
          variantStatus = 'out-of-stock';
        } else if (variantQty <= 5) {
          variantStatus = 'low-stock';
        }
        
        inventoryMap.set(variantNode.id, {
          productId: productNode.id,
          variantId: variantNode.id,
          status: variantStatus,
          quantity: variantQty,
          leadTime: variantStatus === 'out-of-stock' ? '2-3 weeks' : undefined,
        });
      }
    }
  } catch (error) {
    console.error('Failed to fetch inventory:', error);
  }
  
  return inventoryMap;
}

// Create line item attributes for subscriptions
// Prefix with underscore to hide from Shopify checkout (Shopify convention)
function createSubscriptionAttributes(subscription: CartItemSubscription): Array<{ key: string; value: string }> {
  return [
    { key: "_subscription_enabled", value: "true" },
    { key: "_subscription_plan", value: subscription.plan },
    { key: "_subscription_term_months", value: String(subscription.termMonths) },
    { key: "_subscription_frequency", value: subscription.frequency },
    { key: "_subscription_month_index", value: String(subscription.monthIndex) },
    { key: "_subscription_price_per_pack", value: subscription.pricePerPack.toFixed(2) },
    { key: "_subscription_pack_type", value: subscription.packType },
    { key: "_subscription_key", value: subscription.subscriptionKey },
    { key: "_subscription_base_variant_id", value: subscription.baseVariantId },
  ];
}

// Cart Management Functions
export async function createShopifyCart(item: CartItem): Promise<{ cartId: string; checkoutUrl: string; lineId: string } | null> {
  const lineItem: {
    quantity: number;
    merchandiseId: string;
    attributes?: Array<{ key: string; value: string }>;
  } = {
    quantity: item.quantity,
    merchandiseId: item.variantId,
  };

  if (item.subscription?.enabled) {
    lineItem.attributes = createSubscriptionAttributes(item.subscription);
  }

  const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: { lines: [lineItem] },
  });

  if (data?.data?.cartCreate?.userErrors?.length > 0) {
    console.error('Cart creation failed:', data.data.cartCreate.userErrors);
    return null;
  }

  const cart = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;

  const lineId = cart.lines.edges[0]?.node?.id;
  if (!lineId) return null;

  return { 
    cartId: cart.id, 
    checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), 
    lineId 
  };
}

export async function addLineToShopifyCart(
  cartId: string, 
  item: CartItem
): Promise<{ success: boolean; lineId?: string; checkoutUrl?: string; cartNotFound?: boolean }> {
  const lineItem: {
    quantity: number;
    merchandiseId: string;
    attributes?: Array<{ key: string; value: string }>;
  } = {
    quantity: item.quantity,
    merchandiseId: item.variantId,
  };

  if (item.subscription?.enabled) {
    lineItem.attributes = createSubscriptionAttributes(item.subscription);
  }

  const data = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [lineItem],
  });

  const userErrors = data?.data?.cartLinesAdd?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    console.error('Add line failed:', userErrors);
    return { success: false };
  }

  const cart = data?.data?.cartLinesAdd?.cart;
  const lines = cart?.lines?.edges || [];
  const newLine = lines.find((l: { node: { id: string; merchandise: { id: string } } }) => 
    l.node.merchandise.id === item.variantId
  );
  
  return { 
    success: true, 
    lineId: newLine?.node?.id,
    checkoutUrl: cart?.checkoutUrl ? formatCheckoutUrl(cart.checkoutUrl) : undefined
  };
}

export async function updateShopifyCartLine(
  cartId: string, 
  lineId: string, 
  quantity: number
): Promise<{ success: boolean; checkoutUrl?: string; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });

  const userErrors = data?.data?.cartLinesUpdate?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    console.error('Update line failed:', userErrors);
    return { success: false };
  }
  
  const cart = data?.data?.cartLinesUpdate?.cart;
  return { 
    success: true,
    checkoutUrl: cart?.checkoutUrl ? formatCheckoutUrl(cart.checkoutUrl) : undefined
  };
}

export async function removeLineFromShopifyCart(
  cartId: string, 
  lineId: string
): Promise<{ success: boolean; checkoutUrl?: string; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_REMOVE_MUTATION, {
    cartId,
    lineIds: [lineId],
  });

  const userErrors = data?.data?.cartLinesRemove?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    console.error('Remove line failed:', userErrors);
    return { success: false };
  }
  
  const cart = data?.data?.cartLinesRemove?.cart;
  return { 
    success: true,
    checkoutUrl: cart?.checkoutUrl ? formatCheckoutUrl(cart.checkoutUrl) : undefined
  };
}

export async function applyDiscountCode(
  cartId: string, 
  discountCode: string
): Promise<{ success: boolean; applicable?: boolean; cartNotFound?: boolean; error?: string }> {
  const data = await storefrontApiRequest(CART_DISCOUNT_CODES_UPDATE, {
    cartId,
    discountCodes: [discountCode],
  });

  const userErrors = data?.data?.cartDiscountCodesUpdate?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    return { success: false, error: userErrors[0].message };
  }
  
  const cart = data?.data?.cartDiscountCodesUpdate?.cart;
  const appliedCode = cart?.discountCodes?.find((dc: { code: string; applicable: boolean }) => 
    dc.code.toLowerCase() === discountCode.toLowerCase()
  );
  
  return { 
    success: true,
    applicable: appliedCode?.applicable ?? false
  };
}

export async function fetchCart(cartId: string): Promise<{ exists: boolean; totalQuantity?: number; checkoutUrl?: string }> {
  const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
  if (!data) return { exists: false };
  
  const cart = data?.data?.cart;
  if (!cart) return { exists: false };
  
  return { 
    exists: true, 
    totalQuantity: cart.totalQuantity,
    checkoutUrl: cart.checkoutUrl ? formatCheckoutUrl(cart.checkoutUrl) : undefined
  };
}

// Legacy function for backward compatibility - creates cart at checkout time
export async function createStorefrontCheckout(items: CartItem[]): Promise<string> {
  const lines = items.map(item => {
    const lineItem: {
      quantity: number;
      merchandiseId: string;
      attributes?: Array<{ key: string; value: string }>;
    } = {
      quantity: item.quantity,
      merchandiseId: item.variantId,
    };

    if (item.subscription?.enabled) {
      lineItem.attributes = createSubscriptionAttributes(item.subscription);
    }

    return lineItem;
  });

  const cartData = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: { lines },
  });

  if (!cartData) {
    throw new Error('Failed to create cart');
  }

  if (cartData.data.cartCreate.userErrors.length > 0) {
    throw new Error(`Cart creation failed: ${cartData.data.cartCreate.userErrors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  const cart = cartData.data.cartCreate.cart;
  
  if (!cart.checkoutUrl) {
    throw new Error('No checkout URL returned from Shopify');
  }

  return formatCheckoutUrl(cart.checkoutUrl);
}